<script setup lang="ts">
import { ref, computed } from 'vue';

import { useElementSize, useWindowSize } from '@vueuse/core';
import type {
  ReviewWidgetBundle,
} from '@thehotelsnetwork/personalization-types';
import {
  FloatingBox,
  FloatingWindow,
  FloatingTrigger,
  RecordGrid,
  ArrowBtn,
  ActionButton,
  useTheme,
  useI18n,
  useTextDirection,
  useAutoClose,
} from '@thehotelsnetwork/widgets-core';
import { ChevronDownIcon } from '@thehotelsnetwork/icons/vue/linear';

import MainRating from '../components/MainRating.vue';
import ReviewList from '../components/ReviewList.vue';
import PropertyRanking from '../components/PropertyRanking.vue';
import ProvidersInfo from '../components/ProvidersInfo.vue';

import {
  useProvidersItems,
  useTailwindTheme,
  useRankingConfig,
} from '../composables';

import { ViewType } from '../declarations/floating.types';

const props = defineProps<{
  config: ReviewWidgetBundle;
}>();

const expandedHeaderHeight = 38;
const widgetMargin = 40;
const theme = useTheme();
const { t } = useI18n();
const currentView = ref<ViewType>(
  props.config.showClosed ? ViewType.COLLAPSED : ViewType.EXPANDED,
);
const expandedBodyElement = ref<HTMLElement>();
const { height: windowHeight } = useWindowSize();
const {
  height: expandedBodyElementHeight,
} = useElementSize(expandedBodyElement);
const tailwindTheme = useTailwindTheme();
const textDirection = useTextDirection(props.config.isRightAlign);
const { clear: clearAutoClose } = useAutoClose({
  enabled: !props.config.showClosed,
  delay: props.config.closeTime,
  callback() {
    currentView.value = ViewType.COLLAPSED;
  },
});

const widgetSizes = computed(() => ({
  [ViewType.COLLAPSED]: {
    width: '240px',
    height: '52px',
  },
  [ViewType.EXPANDED]: {
    width: '220px',
    height: `${expandedHeaderHeight + expandedBodyElementHeight.value}px`,
  },
  [ViewType.REVIEWS]: {
    width: '520px',
    height: `${Math.min(620, windowHeight.value - widgetMargin)}px`,
  },
}));
const showReviewersList = ref(!props.config.showSummarized);
const widgetSize = computed(() => widgetSizes.value[currentView.value]);
const triggerPosition = computed(() => {
  if (
    props.config.devicePosition === 'UL'
    || props.config.devicePosition === 'UR'
  ) {
    return 'bottom';
  }
  return 'top';
});
const providersItems = useProvidersItems(
  props.config.payload.reviewProviders,
);

const propertyRankingConfig = useRankingConfig(props.config);

function toggleView() {
  clearAutoClose();
  currentView.value = currentView.value === ViewType.EXPANDED
    ? ViewType.COLLAPSED
    : ViewType.EXPANDED;
}
function collapse() {
  clearAutoClose();
  currentView.value = ViewType.COLLAPSED;
}

defineExpose({
  collapse,
});
</script>

<template>
  <FloatingBox
    :position="props.config.devicePosition"
    class="floating-review"
    @mouseenter="clearAutoClose"
  >
    <FloatingWindow
      class="floating-review__window"
    >
      <div
        v-if="currentView === ViewType.EXPANDED
          || currentView === ViewType.COLLAPSED"
      >
        <FloatingTrigger
          :open="currentView !== ViewType.COLLAPSED"
          :position="triggerPosition"
          @click="toggleView"
          :style="{
            height: currentView === ViewType.COLLAPSED
              ? widgetSizes[ViewType.COLLAPSED].height
              : `${expandedHeaderHeight}px`,
          }"
          data-testid="floatingTrigger"
        >
          <Transition
            name="fade"
            mode="out-in"
          >
            <MainRating
              v-if="currentView === ViewType.COLLAPSED"
              :data="props.config.payload.reviewAverages"
              :colors="{
                darkText: theme.mainColor.contrasted,
                lightText: theme.mainColor.contrasted,
              }"
              compact-size="big"
              data-testid="mainRatingCollapsed"
            />
            <h1
              v-else
              class="font-semibold text-s leading-4 text-left"
            >
              {{ t('Review Summary') }}
            </h1>
          </Transition>
        </FloatingTrigger>
        <div
          v-if="currentView === ViewType.EXPANDED"
          ref="expandedBodyElement"
          data-testid="expandedBody"
          role="dialog"
        >
          <div
            :class="[
              'pt-3 px-4',
              { 'pb-4': showReviewersList },
              { 'cursor-pointer': !showReviewersList },
              { 'border-b border-b-grey-100': currentView === ViewType.EXPANDED },
            ]"
            @click="() => {
              if (!showReviewersList) {
                showReviewersList = true;
              }
            }"
          >
            <MainRating
              :data="props.config.payload.reviewAverages"
              :colors="{
                darkText: tailwindTheme.colors.grey[900],
                lightText: tailwindTheme.colors.grey[500],
              }"
              orientation="vertical"
            />
            <PropertyRanking
              v-if="propertyRankingConfig.visible"
              :config="propertyRankingConfig"
              inline
              class="mt-2"
            />
            <button
              v-if="!showReviewersList"
              type="button"
              @click="showReviewersList = true"
              :aria-label="t('Show Reviewers')"
              class="w-full mt-2 border-0 outline-none text-grey-500 hover:text-grey-900 transition-colors duration-300"
            >
              <ChevronDownIcon
                class="w-4 h-4 mx-auto"
              />
            </button>
          </div>
          <div
            v-if="showReviewersList"
            class="py-3 px-4"
          >
            <RecordGrid
              :items="providersItems"
              direction="column"
              class="mb-4 leading-6"
            >
              <template #value="{ item }">
                <strong class="text-base">{{ item.value.score }}</strong>
                <span class="text-grey-500">/{{ item.value.max }}</span>
              </template>
            </RecordGrid>
            <ArrowBtn
              v-if="props.config.showReviews"
              @click="currentView = ViewType.REVIEWS; clearAutoClose()"
              class="text-sm"
            >
              {{ t('Read Reviews') }}
            </ArrowBtn>
          </div>
        </div>
      </div>
      <Transition
        name="fade"
        appear
      >
        <div
          v-if="currentView === ViewType.REVIEWS"
          class="flex flex-col h-full"
          role="dialog"
          data-testid="reviewsList"
        >
          <div
            class="flex items-center p-3 gap-4 reviews-header"
          >
            <ActionButton
              type="back"
              @action-click="currentView = ViewType.EXPANDED"
            />
            <MainRating
              :data="props.config.payload.reviewAverages"
              :colors="{
                darkText: tailwindTheme.colors.grey[900],
                lightText: tailwindTheme.colors.grey[500],
              }"
              orientation="horizontal"
              show-info-btn
              tooltip-placement="bottom"
              class="flex-1"
            >
              <template #providersInfo>
                <ProvidersInfo
                  :data="providersItems"
                  :direction="textDirection"
                />
              </template>
            </MainRating>
            <ActionButton
              type="close"
              @action-click="currentView = ViewType.COLLAPSED"
            />
          </div>
          <ReviewList
            :reviews="props.config.payload.reviews"
            class="flex-1 min-h-0"
          />
        </div>
      </Transition>
    </FloatingWindow>
  </FloatingBox>
</template>

<style scoped>
.floating-review {
  z-index: v-bind(props.config.zIndex);
  color: v-bind(theme.fontColor);
}
.floating-review__window {
  width: v-bind(widgetSize.width);
  height: v-bind(widgetSize.height);
  overflow: hidden;
  transition: all 400ms ease;
  margin: v-bind(`${widgetMargin}px`);
}
.color-action {
  color: v-bind(theme.actionColor.base);
}
.reviews-header {
  background-color: v-bind(theme.backgroundColor.washed);
}
</style>
